import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Card } from '../../components/ui/Card';
import { SubscriptionStatus } from '../../components/subscription/SubscriptionStatus';
import { VPNKeyDisplay } from '../../components/subscription/VPNKeyDisplay';
import { TrialCard } from '../../components/subscription/TrialCard';
import { InactiveStateCard } from '../../components/subscription/InactiveStateCard';
import { BuyExtraKeyCard } from '../../components/subscription/BuyExtraKeyCard';
import { TelegramConnect } from '../../components/telegram/TelegramConnect';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { Spinner } from '../../components/ui/Spinner';
import styles from './DashboardPage.module.css';

export function DashboardPage() {
  const { user } = useAuth();
  const {
    plans,
    currentSubscription,
    keys,
    trialStatus,
    trialConfig,
    cdnQuota,
    loading,
    fetchAll,
    fetchKeys,
    fetchTrial,
    updateKey,
    updateTrial,
  } = useSubscriptionStore();

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <div className={styles.page} style={{ textAlign: 'center', paddingTop: '4rem' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  const hasActiveSubscription = currentSubscription?.status === 'active';
  const trial = trialStatus?.trial ?? null;
  const showTrialCard = !hasActiveSubscription && (trial !== null || trialStatus?.eligible === true);
  const showInactiveState =
    !hasActiveSubscription && trial === null && trialStatus?.eligible === false;
  const keysToShow = hasActiveSubscription ? keys : keys.filter((k) => k.isActive);

  // Докупка ключа: только при активной платной подписке и extra_key_price_kopecks > 0
  const currentPlan = hasActiveSubscription
    ? plans.find((p) => p.id === currentSubscription?.planId)
    : undefined;
  const extraKeyPriceKopecks = currentPlan?.extraKeyPriceKopecks ?? 0;
  const showBuyExtraKey = hasActiveSubscription && extraKeyPriceKopecks > 0;

  return (
    <div className={styles.page}>
      <h1 className={styles.greeting}>
        Добро пожаловать{user?.email ? `, ${user.email}` : ''}!
      </h1>
      <p className={styles.subtitle}>Управляйте подпиской и VPN-ключами</p>

      {hasActiveSubscription && (
        <div className={styles.subscriptionBar}>
          <SubscriptionStatus
            subscription={currentSubscription}
            cdnQuota={cdnQuota}
            onTopupPurchased={() => fetchAll()}
          />
        </div>
      )}

      <div className={styles.grid}>
        {showTrialCard && (
          <TrialCard
            trial={trial}
            hasSubscription={hasActiveSubscription}
            trialConfig={trialConfig}
            cdnQuota={cdnQuota}
            onActivated={() => fetchAll()}
            onDeviceUnbound={(updated) => {
              // Сразу убираем устройство с карточки, затем тихо перечитываем триал с сервера
              updateTrial(updated);
              void fetchTrial();
            }}
          />
        )}

        {keysToShow.length > 0 && keysToShow.map((key) => (
          <VPNKeyDisplay
            key={key.id}
            vpnKey={key}
            onRenamed={(updated) => updateKey(updated)}
            onDeviceUnbound={(updated) => {
              // Сразу убираем устройство с карточки, затем тихо перечитываем список ключей
              updateKey(updated);
              void fetchKeys();
            }}
          />
        ))}

        {showBuyExtraKey && (
          <BuyExtraKeyCard
            priceKopecks={extraKeyPriceKopecks}
            onPurchased={() => fetchAll()}
          />
        )}

        {showInactiveState && <InactiveStateCard />}

        {(trial || hasActiveSubscription) && !user?.telegramLinked && (
          <TelegramConnect />
        )}
      </div>

      <div className={styles.quickActions}>
        <h2 className={styles.quickActionsTitle}>Быстрые действия</h2>
        <div className={styles.actionsGrid}>
          {!showInactiveState && (
            <Link to="/dashboard/plans" style={{ textDecoration: 'none' }}>
              <Card className={styles.actionCard}>
                <div className={styles.actionIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                </div>
                <div className={styles.actionText}>
                  <h4>{hasActiveSubscription ? 'Продлить подписку' : 'Оформить подписку'}</h4>
                  <p>Выберите тариф и подключитесь</p>
                </div>
              </Card>
            </Link>
          )}

          <Link to="/dashboard/referral" style={{ textDecoration: 'none' }}>
            <Card className={styles.actionCard}>
              <div className={styles.actionIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className={styles.actionText}>
                <h4>Пригласить друга</h4>
                <p>Бонус за друга — по вашему тарифу (до +7 дней)</p>
              </div>
            </Card>
          </Link>

          <Link to="/dashboard/support" style={{ textDecoration: 'none' }}>
            <Card className={styles.actionCard}>
              <div className={styles.actionIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className={styles.actionText}>
                <h4>Поддержка</h4>
                <p>Задайте вопрос нашей команде</p>
              </div>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
}
